import {
  buildSearchExpressionStatePatch,
  normalizeSearchExpression,
} from "./map-search-contract.js";
import { assertMapShellProjectionV1 } from "./map-shell-projection.js";
import { augmentTradeNpcQueryActivationPatch } from "./map-trade-npc-query-activation.js";

export const FISHYMAP_QUERY_EDITOR_STATE_REQUESTED_EVENT =
  "fishymap:query-editor-state-requested";
export const FISHYMAP_QUERY_ACTIVATION_REQUESTED_EVENT =
  "fishymap:query-activation-requested";
export const FISHYMAP_QUERY_RESTORED_EFFECTS_REQUESTED_EVENT =
  "fishymap:query-restored-effects-requested";
export const FISHYMAP_TUTORIAL_HELP_WINDOW_REQUESTED_EVENT =
  "fishymap:tutorial-help-window-requested";
export const FISHYMAP_QUERY_INPUT_RECEIPT_OWNER = "map-query-input";

const REQUEST_VERSION = 1;
const MAX_REQUEST_JSON_DEPTH = 32;
const MAX_REQUEST_JSON_NODES = 4_096;
const MAX_REQUEST_COLLECTION_ENTRIES = 8_192;
const MAX_REQUEST_STRING_BYTES = 65_536;
const MAX_REQUEST_KEY_BYTES = 256;
const MAX_REQUEST_DETAIL_BYTES = 131_072;
const QUERY_EDITOR_STATES = new Set(["editing", "idle"]);
const TUTORIAL_HELP_TRANSITIONS = new Set([
  "suspend",
  "close-expanded",
  "restore",
]);

function isPlainObject(value) {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function assertExactKeys(value, keys, label) {
  if (!isPlainObject(value)) {
    throw new TypeError(`${label} must be an object`);
  }
  const expected = new Set(keys);
  const unsupported = Object.keys(value).find((key) => !expected.has(key));
  if (unsupported) {
    throw new TypeError(`${label} has unsupported field: ${unsupported}`);
  }
  const missing = keys.find((key) => !Object.prototype.hasOwnProperty.call(value, key));
  if (missing) {
    throw new TypeError(`${label} is missing field: ${missing}`);
  }
}

function assertVersion(value, label) {
  if (value !== REQUEST_VERSION) {
    throw new TypeError(`${label} version must be ${REQUEST_VERSION}`);
  }
}

function deepEqualJson(left, right) {
  if (Object.is(left, right)) {
    return true;
  }
  if (Array.isArray(left) || Array.isArray(right)) {
    return Array.isArray(left) &&
      Array.isArray(right) &&
      left.length === right.length &&
      left.every((value, index) => deepEqualJson(value, right[index]));
  }
  if (!isPlainObject(left) || !isPlainObject(right)) {
    return false;
  }
  const leftKeys = Object.keys(left).sort();
  const rightKeys = Object.keys(right).sort();
  return leftKeys.length === rightKeys.length &&
    leftKeys.every((key, index) =>
      key === rightKeys[index] && deepEqualJson(left[key], right[key])
    );
}

function utf8ByteLength(value) {
  let byteLength = 0;
  for (const character of String(value)) {
    const codePoint = character.codePointAt(0);
    byteLength += codePoint <= 0x7f
      ? 1
      : codePoint <= 0x7ff
        ? 2
        : codePoint <= 0xffff
          ? 3
          : 4;
  }
  return byteLength;
}

function assertFiniteJson(
  value,
  label,
  budget = { nodes: 0, collectionEntries: 0 },
  ancestors = new WeakSet(),
  depth = 0,
) {
  budget.nodes += 1;
  if (budget.nodes > MAX_REQUEST_JSON_NODES) {
    throw new TypeError(`${label} exceeds the aggregate JSON node limit`);
  }
  if (depth > MAX_REQUEST_JSON_DEPTH) {
    throw new TypeError(`${label} exceeds the maximum nesting depth`);
  }
  if (typeof value === "string") {
    if (utf8ByteLength(value) > MAX_REQUEST_STRING_BYTES) {
      throw new TypeError(`${label} contains a string that exceeds the UTF-8 byte limit`);
    }
    return;
  }
  if (value === null || typeof value === "boolean") {
    return;
  }
  if (typeof value === "number") {
    if (!Number.isFinite(value)) {
      throw new TypeError(`${label} numbers must be finite`);
    }
    return;
  }
  if (!Array.isArray(value) && !isPlainObject(value)) {
    throw new TypeError(`${label} must contain only JSON data`);
  }
  if (ancestors.has(value)) {
    throw new TypeError(`${label} must not contain cycles`);
  }
  ancestors.add(value);
  const entries = Array.isArray(value) ? value : Object.entries(value);
  budget.collectionEntries += entries.length;
  if (budget.collectionEntries > MAX_REQUEST_COLLECTION_ENTRIES) {
    throw new TypeError(`${label} exceeds the aggregate JSON collection-entry limit`);
  }
  for (const entry of entries) {
    const [key, child] = Array.isArray(value) ? [null, entry] : entry;
    if (key !== null && utf8ByteLength(key) > MAX_REQUEST_KEY_BYTES) {
      throw new TypeError(`${label} contains a key that exceeds the UTF-8 byte limit`);
    }
    assertFiniteJson(child, label, budget, ancestors, depth + 1);
  }
  ancestors.delete(value);
}

function deepFreeze(value) {
  if (!value || typeof value !== "object" || Object.isFrozen(value)) {
    return value;
  }
  for (const child of Object.values(value)) {
    deepFreeze(child);
  }
  return Object.freeze(value);
}

function frozenJsonClone(value, label) {
  let cloned;
  try {
    cloned = JSON.parse(JSON.stringify(value));
  } catch (_error) {
    throw new TypeError(`${label} must be finite JSON data`);
  }
  return deepFreeze(cloned);
}

function normalizeCanonicalSearchExpression(value) {
  if (!isPlainObject(value) || value.type !== "group") {
    throw new TypeError("Map query activation expression must be a canonical root group");
  }
  assertFiniteJson(value, "Map query activation expression");
  if (utf8ByteLength(JSON.stringify(value)) > MAX_REQUEST_DETAIL_BYTES) {
    throw new TypeError(
      "Map query activation expression exceeds the 131072-byte intent limit",
    );
  }
  const normalized = normalizeSearchExpression(value);
  if (!normalized || !deepEqualJson(value, normalized)) {
    throw new TypeError("Map query activation expression must use the canonical schema");
  }
  return frozenJsonClone(normalized, "Map query activation expression");
}

function bridgeInputSignalsFromProjection(value) {
  const projection = assertMapShellProjectionV1(value);
  return {
    _map_ui: projection.ui,
    _map_bridged: projection.bridged,
    _map_actions: projection.actions,
    _map_session: projection.session,
  };
}

function dispatchRequest(
  target,
  eventType,
  detail,
  customEventConstructor = globalThis.CustomEvent,
) {
  if (
    !target ||
    typeof target.dispatchEvent !== "function" ||
    typeof customEventConstructor !== "function"
  ) {
    return false;
  }
  return target.dispatchEvent(
    new customEventConstructor(eventType, {
      bubbles: true,
      composed: true,
      cancelable: true,
      detail,
    }),
  );
}

export function normalizeMapQueryEditorStateRequest(value) {
  const label = "Map query editor-state request";
  assertExactKeys(value, ["version", "state"], label);
  assertVersion(value.version, label);
  if (!QUERY_EDITOR_STATES.has(value.state)) {
    throw new TypeError(`${label} state must be editing or idle`);
  }
  return Object.freeze({ version: REQUEST_VERSION, state: value.state });
}

export function dispatchMapQueryEditorStateRequest(
  target,
  state,
  customEventConstructor = globalThis.CustomEvent,
) {
  const detail = normalizeMapQueryEditorStateRequest({
    version: REQUEST_VERSION,
    state,
  });
  return dispatchRequest(
    target,
    FISHYMAP_QUERY_EDITOR_STATE_REQUESTED_EVENT,
    detail,
    customEventConstructor,
  );
}

export function mapQueryEditorStateRequestToSignalPatch(value) {
  const request = normalizeMapQueryEditorStateRequest(value);
  return deepFreeze({
    _map_ui: {
      search: { open: request.state === "editing" },
    },
  });
}

export function normalizeMapQueryActivationRequest(value) {
  const label = "Map query activation request";
  assertExactKeys(
    value,
    ["version", "activationRevision", "expression", "editorState"],
    label,
  );
  assertVersion(value.version, label);
  if (!Number.isSafeInteger(value.activationRevision) || value.activationRevision <= 0) {
    throw new TypeError(`${label} activationRevision must be a positive safe integer`);
  }
  if (!QUERY_EDITOR_STATES.has(value.editorState)) {
    throw new TypeError(`${label} editorState must be editing or idle`);
  }
  return Object.freeze({
    version: REQUEST_VERSION,
    activationRevision: value.activationRevision,
    expression: normalizeCanonicalSearchExpression(value.expression),
    editorState: value.editorState,
  });
}

export function dispatchMapQueryActivationRequest(
  target,
  request,
  customEventConstructor = globalThis.CustomEvent,
) {
  return dispatchRequest(
    target,
    FISHYMAP_QUERY_ACTIVATION_REQUESTED_EVENT,
    normalizeMapQueryActivationRequest({ version: REQUEST_VERSION, ...request }),
    customEventConstructor,
  );
}

export function mapQueryActivationRequestToSignalPatch(
  value,
  projection,
  { tradeNpcCatalog = null } = {},
) {
  const request = normalizeMapQueryActivationRequest(value);
  const signals = bridgeInputSignalsFromProjection(projection);
  return deepFreeze(augmentTradeNpcQueryActivationPatch(
    buildSearchExpressionStatePatch(request.expression, {
      open: request.editorState === "editing",
    }),
    {
      signals,
      previousExpression: projection.ui?.search?.expression,
      nextExpression: request.expression,
      tradeNpcCatalog,
    },
  ));
}

export function normalizeMapQueryRestoredEffectsRequest(value) {
  const label = "Map restored-query effects request";
  assertExactKeys(value, ["version", "effect"], label);
  assertVersion(value.version, label);
  if (value.effect !== "trade-npc") {
    throw new TypeError(`${label} effect must be trade-npc`);
  }
  return Object.freeze({ version: REQUEST_VERSION, effect: "trade-npc" });
}

export function dispatchMapQueryRestoredEffectsRequest(
  target,
  customEventConstructor = globalThis.CustomEvent,
) {
  return dispatchRequest(
    target,
    FISHYMAP_QUERY_RESTORED_EFFECTS_REQUESTED_EVENT,
    normalizeMapQueryRestoredEffectsRequest({
      version: REQUEST_VERSION,
      effect: "trade-npc",
    }),
    customEventConstructor,
  );
}

export function mapQueryRestoredEffectsRequestToSignalPatch(
  value,
  projection,
  { tradeNpcCatalog = null } = {},
) {
  normalizeMapQueryRestoredEffectsRequest(value);
  const signals = bridgeInputSignalsFromProjection(projection);
  return deepFreeze(augmentTradeNpcQueryActivationPatch(
    {},
    {
      signals,
      previousExpression: null,
      nextExpression: projection.ui?.search?.expression,
      tradeNpcCatalog,
    },
  ));
}

export function normalizeMapTutorialHelpWindowRequest(value) {
  const label = "Map tutorial Help-window request";
  if (!isPlainObject(value) || !TUTORIAL_HELP_TRANSITIONS.has(value.transition)) {
    throw new TypeError(
      `${label} transition must be suspend, close-expanded, or restore`,
    );
  }
  const restore = value.transition === "restore";
  assertExactKeys(
    value,
    restore ? ["version", "transition", "state"] : ["version", "transition"],
    label,
  );
  assertVersion(value.version, label);
  if (!restore) {
    return Object.freeze({ version: REQUEST_VERSION, transition: value.transition });
  }
  assertExactKeys(value.state, ["open", "collapsed"], `${label} state`);
  if (typeof value.state.open !== "boolean" || typeof value.state.collapsed !== "boolean") {
    throw new TypeError(`${label} state values must be boolean`);
  }
  return Object.freeze({
    version: REQUEST_VERSION,
    transition: "restore",
    state: Object.freeze({
      open: value.state.open,
      collapsed: value.state.collapsed,
    }),
  });
}

export function dispatchMapTutorialHelpWindowRequest(
  target,
  request,
  customEventConstructor = globalThis.CustomEvent,
) {
  return dispatchRequest(
    target,
    FISHYMAP_TUTORIAL_HELP_WINDOW_REQUESTED_EVENT,
    normalizeMapTutorialHelpWindowRequest({ version: REQUEST_VERSION, ...request }),
    customEventConstructor,
  );
}

export function mapTutorialHelpWindowRequestToSignalPatch(value) {
  const request = normalizeMapTutorialHelpWindowRequest(value);
  const help = request.transition === "suspend"
    ? { open: false }
    : request.transition === "close-expanded"
      ? { open: false, collapsed: false }
      : { open: request.state.open, collapsed: request.state.collapsed };
  return deepFreeze({
    _map_ui: {
      windowUi: { help },
    },
  });
}
